import React from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { getUserFriendlyError, ErrorContext } from '../utils/userFriendlyErrors';
import { FaExternalLinkAlt, FaArrowUp, FaCrown } from 'react-icons/fa';

interface UserFriendlyErrorToastProps {
  toastRef: React.RefObject<Toast>;
}

export const UserFriendlyErrorToast: React.FC<UserFriendlyErrorToastProps> = ({ toastRef }) => {
  const navigate = useNavigate();

  React.useImperativeHandle(toastRef, () => {
    const toastInstance = toastRef.current;
    if (!toastInstance) {
      return null as any;
    }
    return {
      ...toastInstance,
      showUserFriendlyError: (error: any, context: ErrorContext = {}) => showUserFriendlyError(error, context)
    };
  });

  const showUserFriendlyError = (error: any, context: ErrorContext = {}) => {
    const friendlyError = getUserFriendlyError(error, context);

    // Create custom content with action button if available
    const content = (
      <div className="flex flex-col space-y-3">
        <div>
          <div className="font-semibold text-gray-900 mb-1">{friendlyError.title}</div>
          <div className="text-gray-700 text-sm leading-relaxed">{friendlyError.message}</div>
        </div>

        {(friendlyError.actionText || friendlyError.showUpgrade) && (
          <div className="flex space-x-2">
            {friendlyError.actionText && friendlyError.actionUrl && (
              <Button
                label={friendlyError.actionText}
                icon={friendlyError.actionUrl.startsWith('http') || friendlyError.actionUrl.startsWith('mailto') ?
                  <FaExternalLinkAlt /> : undefined}
                onClick={() => {
                  if (friendlyError.actionUrl!.startsWith('http') || friendlyError.actionUrl!.startsWith('mailto')) {
                    window.open(friendlyError.actionUrl, '_blank');
                  } else {
                    navigate(friendlyError.actionUrl!);
                  }
                }}
                className="p-button-sm p-button-outlined"
                size="small"
              />
            )}

            {friendlyError.showUpgrade && !friendlyError.actionUrl?.includes('pricing') && (
              <Button
                label="Upgrade Plan"
                icon={<FaCrown />}
                onClick={() => navigate('/billing')}
                className="p-button-sm"
                size="small"
              />
            )}
          </div>
        )}
      </div>
    );

    // Show the toast with appropriate severity
    toastRef.current?.show({
      severity: friendlyError.severity,
      summary: '', // We'll use custom content instead
      detail: content,
      life: friendlyError.severity === 'error' ? 8000 : 6000, // Longer for errors
      closable: true
    });
  };

  return (
    <Toast
      ref={toastRef}
      position="top-right"
      className="custom-toast"
    />
  );
};

// Hook for easy usage
export const useUserFriendlyError = () => {
  const navigate = useNavigate();

  const showError = (error: any, context: ErrorContext = {}, toastRef?: React.RefObject<Toast>) => {
    const friendlyError = getUserFriendlyError(error, context);

    if (toastRef?.current) {
      // Create custom content
      const content = (
        <div className="flex flex-col space-y-3">
          <div>
            <div className="font-semibold text-gray-900 mb-1">{friendlyError.title}</div>
            <div className="text-gray-700 text-sm leading-relaxed">{friendlyError.message}</div>
          </div>

          {(friendlyError.actionText || friendlyError.showUpgrade) && (
            <div className="flex space-x-2">
              {friendlyError.actionText && friendlyError.actionUrl && (
                <button
                  onClick={() => {
                    if (friendlyError.actionUrl!.startsWith('http') || friendlyError.actionUrl!.startsWith('mailto')) {
                      window.open(friendlyError.actionUrl, '_blank');
                    } else {
                      navigate(friendlyError.actionUrl!);
                    }
                  }}
                  className="inline-flex items-center px-3 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 transition-colors"
                >
                  {friendlyError.actionUrl!.startsWith('http') || friendlyError.actionUrl!.startsWith('mailto') ?
                    <FaExternalLinkAlt className="mr-1" size={10} /> :
                    <FaArrowUp className="mr-1" size={10} />
                  }
                  {friendlyError.actionText}
                </button>
              )}

              {friendlyError.showUpgrade && !friendlyError.actionUrl?.includes('pricing') && (
                <button
                  onClick={() => navigate('/billing')}
                  className="inline-flex items-center px-3 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 transition-colors"
                >
                  <FaCrown className="mr-1" size={10} />
                  Upgrade Plan
                </button>
              )}
            </div>
          )}
        </div>
      );

      toastRef.current.show({
        severity: friendlyError.severity,
        summary: '',
        detail: content,
        life: friendlyError.severity === 'error' ? 8000 : 6000,
        closable: true
      });
    }

    return friendlyError;
  };

  return { showError };
};
